import { throwErrorMessage } from 'dashboard/store/utils/api';
import { scheduleSafeTimeout } from 'dashboard/helper/browserTimers';
import { dispatchSafeWindowCustomEvent } from 'dashboard/helper/domEvents';
import ConversationApi from '../../../../api/inbox/conversation';
import {
  buildConversationUnreadPatch,
  CONVERSATION_READ_STATE_PENDING_TTL_MS,
} from '../../../../helper/conversationUnreadState';
import mutationTypes from '../../../mutation-types';

// Definizione delle costanti di stato
const CONVERSATION_STATUS = {
  READ: 'read',
  UNREAD: 'unread',
};

const currentUnixTimestamp = () => Math.floor(Date.now() / 1000);
const readRequestsInFlight = new Map();
const unreadRequestsInFlight = new Map();
const latestReadStateRequestTokens = new Map();
let readStateRequestSequence = 0;

const requestKey = (id) => String(id);
const hasOwn = (object = {}, key = '') =>
  Object.prototype.hasOwnProperty.call(object, key);

const createReadStateRequestToken = () => {
  readStateRequestSequence += 1;
  return readStateRequestSequence;
};

const markLatestReadStateRequest = (key, token) => {
  latestReadStateRequestTokens.set(key, token);
};

const isLatestReadStateRequest = (key, token) =>
  latestReadStateRequestTokens.get(key) === token;

const clearLatestReadStateRequest = (key, token) => {
  if (isLatestReadStateRequest(key, token)) {
    latestReadStateRequestTokens.delete(key);
  }
};

const schedulePendingReadStateClear = ({ commit, id, startedAt }) => {
  scheduleSafeTimeout(() => {
    commit(mutationTypes.CLEAR_CONVERSATION_READ_STATE_OPERATION, {
      id,
      startedAt,
    });
  }, CONVERSATION_READ_STATE_PENDING_TTL_MS);
};

const setPendingReadState = ({ agentLastSeenAt, commit, id }) => {
  const nowMs = Date.now();
  commit(mutationTypes.SET_CONVERSATION_READ_STATE_OPERATION, {
    agentLastSeenAt,
    id,
    nowMs,
  });
  return nowMs;
};

const commitReadState = ({ agentLastSeenAt, commit, id }) => {
  commit(mutationTypes.UPDATE_CONVERSATION, {
    id,
    ...buildConversationUnreadPatch({ lastSeen: agentLastSeenAt }),
  });
};

const runSingleFlight = ({ factory, key, requests }) => {
  const activeRequest = requests.get(key);
  if (activeRequest) {
    markLatestReadStateRequest(key, activeRequest.token);
    return activeRequest.promise;
  }

  const token = createReadStateRequestToken();
  markLatestReadStateRequest(key, token);
  const request = factory({ token }).finally(() => {
    requests.delete(key);
  });
  requests.set(key, { promise: request, token });
  return request;
};

// Refresh label unread dopo mark read/unread (delay per attendere Labels::UpdateCountsJob)
const scheduleLabelsRefresh = (dispatch, rootGetters, inboxId) => {
  if (!inboxId) return;
  const inbox = rootGetters['inboxes/getInbox'](inboxId);
  if (inbox?.channel_type === 'Channel::Email') {
    scheduleSafeTimeout(() => {
      dispatch('inboxes/fetchInboxLabels', inboxId, { root: true });
    }, 2000);
  } else {
    scheduleSafeTimeout(() => {
      dispatch('inboxes/refreshUnreadStatus', null, { root: true });
    }, 2000);
  }
};

export default {
  markMessagesRead: async (
    { commit, dispatch, getters, rootGetters },
    data
  ) => {
    const id = data?.id;
    if (id === null || id === undefined || id === '') {
      return null;
    }
    const key = requestKey(id);

    return runSingleFlight({
      key,
      requests: readRequestsInFlight,
      factory: async ({ token }) => {
        const optimisticLastSeen = currentUnixTimestamp();
        const startedAt = setPendingReadState({
          agentLastSeenAt: optimisticLastSeen,
          commit,
          id,
        });
        const conversation = getters.getConversationById(id);
        const previousAgentLastSeenAt = hasOwn(data, 'previousAgentLastSeenAt')
          ? data.previousAgentLastSeenAt
          : conversation?.agent_last_seen_at;

        if (conversation) {
          commitReadState({
            agentLastSeenAt: optimisticLastSeen,
            commit,
            id,
          });
        }

        try {
          const response = await ConversationApi.markMessageRead(data);
          if (!isLatestReadStateRequest(key, token)) {
            return null;
          }
          const responseId = response?.data?.id || id;
          const lastSeen =
            response?.data?.agent_last_seen_at ?? optimisticLastSeen;
          const confirmedAt = setPendingReadState({
            agentLastSeenAt: lastSeen,
            commit,
            id: responseId,
          });
          const updatedConversation = getters.getConversationById(responseId);

          if (updatedConversation) {
            commitReadState({
              agentLastSeenAt: lastSeen,
              commit,
              id: responseId,
            });
          }

          // Refresh sidebar unread indicator
          if (updatedConversation && updatedConversation.inbox_id) {
            scheduleLabelsRefresh(
              dispatch,
              rootGetters,
              updatedConversation.inbox_id
            );
          }

          schedulePendingReadStateClear({
            commit,
            id: responseId,
            startedAt: confirmedAt,
          });

          // Rimuoviamo eventuale pending ChatList dopo pochi secondi
          scheduleSafeTimeout(() => {
            dispatchSafeWindowCustomEvent('conversation-read-finished', {
              id: responseId,
            });
          }, 1000);
          clearLatestReadStateRequest(key, token);

          return {
            agent_last_seen_at: lastSeen,
            id: responseId,
            status: 'updated',
          };
        } catch (error) {
          if (isLatestReadStateRequest(key, token)) {
            commit(mutationTypes.CLEAR_CONVERSATION_READ_STATE_OPERATION, {
              id,
              startedAt,
            });
            if (conversation && previousAgentLastSeenAt !== undefined) {
              commitReadState({
                agentLastSeenAt: previousAgentLastSeenAt,
                commit,
                id,
              });
            }
            clearLatestReadStateRequest(key, token);
            throwErrorMessage(error);
          }
          return null;
        }
      },
    });
  },

  markMessagesUnread: async (
    { commit, dispatch, getters, rootGetters },
    data = {}
  ) => {
    const id = data?.id;
    if (id === null || id === undefined || id === '') {
      return null;
    }
    const key = requestKey(id);

    return runSingleFlight({
      key,
      requests: unreadRequestsInFlight,
      factory: async ({ token }) => {
        const startedAt = setPendingReadState({
          agentLastSeenAt: null,
          commit,
          id,
        });
        const conversation = getters.getConversationById(id);
        const previousAgentLastSeenAt = hasOwn(data, 'previousAgentLastSeenAt')
          ? data.previousAgentLastSeenAt
          : conversation?.agent_last_seen_at;

        if (conversation) {
          commitReadState({
            agentLastSeenAt: null,
            commit,
            id,
          });
        }

        try {
          await ConversationApi.markMessagesUnread({ id });
          if (!isLatestReadStateRequest(key, token)) {
            return null;
          }
          const confirmedAt = setPendingReadState({
            agentLastSeenAt: null,
            commit,
            id,
          });
          const updatedConversation = getters.getConversationById(id);

          if (updatedConversation) {
            commitReadState({
              agentLastSeenAt: null,
              commit,
              id,
            });
          }

          // Refresh sidebar unread indicator
          if (updatedConversation && updatedConversation.inbox_id) {
            scheduleLabelsRefresh(
              dispatch,
              rootGetters,
              updatedConversation.inbox_id
            );
          }

          schedulePendingReadStateClear({
            commit,
            id,
            startedAt: confirmedAt,
          });
          clearLatestReadStateRequest(key, token);

          return { id, status: 'updated' };
        } catch (error) {
          if (isLatestReadStateRequest(key, token)) {
            commit(mutationTypes.CLEAR_CONVERSATION_READ_STATE_OPERATION, {
              id,
              startedAt,
            });
            if (conversation && previousAgentLastSeenAt !== undefined) {
              commitReadState({
                agentLastSeenAt: previousAgentLastSeenAt,
                commit,
                id,
              });
            }
            clearLatestReadStateRequest(key, token);
            throwErrorMessage(error);
          }
          return null;
        }
      },
    });
  },

  toggleRead: async (
    { commit, dispatch, getters, rootGetters },
    { id, lastSeen }
  ) => {
    try {
      await ConversationApi.toggleStatus({
        conversationId: id,
        status: CONVERSATION_STATUS.READ,
        lastSeen,
      });
      const conversation = getters.getConversationById(id);

      if (conversation) {
        commit(mutationTypes.UPDATE_CONVERSATION, {
          id,
          agent_last_seen_at: lastSeen,
        });
      }

      // Refresh sidebar unread indicator
      if (conversation && conversation.inbox_id) {
        scheduleLabelsRefresh(dispatch, rootGetters, conversation.inbox_id);
      }
    } catch (error) {
      // Handle error
    }
  },

  toggleUnread: async ({ commit, dispatch, rootGetters }, { id }) => {
    try {
      // Prima chiamiamo l'API per segnare la conversazione come non letta
      await ConversationApi.toggleStatus({
        conversationId: id,
        status: CONVERSATION_STATUS.UNREAD,
      });

      // Dopo il successo dell'API, forziamo un aggiornamento della conversazione
      // per ottenere i dati più recenti inclusi tutti gli stati
      const conversation = await ConversationApi.show(id);

      // Aggiorniamo subito lo stato locale usando agent_last_seen_at come fonte.
      commit(mutationTypes.UPDATE_CONVERSATION, {
        id,
        agent_last_seen_at: null,
      });

      // Aggiorniamo completamente la conversazione per assicurarci che tutti gli stati siano coerenti
      commit('updateConversation', conversation.data);

      // Refresh sidebar unread indicator
      if (conversation.data && conversation.data.inbox_id) {
        scheduleLabelsRefresh(
          dispatch,
          rootGetters,
          conversation.data.inbox_id
        );
      }

      // Attendiamo un momento e poi forziamo un aggiornamento dell'elenco delle conversazioni
      scheduleSafeTimeout(() => {
        dispatch('fetchAllConversations', {}, { root: true });
      }, 500);

      return { success: true, data: conversation.data };
    } catch (error) {
      return { success: false, error };
    }
  },

  toggleStarred: async ({ commit }, { conversationId, starred }) => {
    try {
      // Aggiornamento ottimistico immediato per UX reattiva
      commit('updateConversationStarred', { id: conversationId, starred });

      // Chiamata API
      const response = await ConversationApi.toggleStarred({
        conversationId,
        starred,
      });

      if (response.data?.success) {
        // Conferma lo stato - già aggiornato ottimisticamente
        return { success: true, data: response.data };
      }
      // Se l'API non ha avuto successo, ripristina lo stato
      commit('updateConversationStarred', {
        id: conversationId,
        starred: !starred,
      });
      throw new Error(response.data?.error || 'Failed to update starred state');
    } catch (error) {
      // Ripristina lo stato in caso di errore
      commit('updateConversationStarred', {
        id: conversationId,
        starred: !starred,
      });
      throwErrorMessage(error);
      return { success: false, error };
    }
  },
};
